import React, { useCallback, useRef } from 'react';

/* Local-day key (e.g. "2026-05-07") — uses local time, not UTC, so the key
   matches the user's calendar day. */
export const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const draftKey = (uid) => `reflection-draft-${uid || 'me'}-${todayKey()}`;

/* Toast helper — defers to the app-level showHxToast hook if installed,
   otherwise no-ops (so unit tests don't blow up). */
export const flashToast = (msg, kind = 'info') => {
  if (typeof window !== 'undefined' && typeof window.showHxToast === 'function') {
    window.showHxToast(msg, kind);
  }
};

export const formatTime = (ts) => {
  try {
    return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch (_) { return ''; }
};

/* Render {{...}} chunks as inline gold marks.
   Handles both legacy {{gold}}phrase{{/gold}} and new {{phrase}} formats. */
export function renderGoldMarks(text) {
  if (text == null) return null;
  const safe = typeof text === 'string' ? text : String(text);
  const normalized = safe.replace(/\{\{gold\}\}(.*?)\{\{\/gold\}\}/g, '{{$1}}');
  const parts = normalized.split(/(\{\{[^}]+\}\})/g);
  return parts.map((p, i) => {
    if (/^\{\{[^}]+\}\}$/.test(p)) {
      const inner = p.slice(2, -2);
      return <em key={i} className="gold-mark">{inner}</em>;
    }
    return <span key={i}>{p}</span>;
  });
}

export const formatTimeWindow = (tw) => {
  if (!tw) return null;
  if (typeof tw === 'string') return tw;
  if (tw.start && tw.end) return `${tw.start} — ${tw.end}`;
  return null;
};

/* debounce */
export const useDebouncedCallback = (cb, ms) => {
  const ref = useRef();
  return useCallback((...args) => {
    if (ref.current) clearTimeout(ref.current);
    ref.current = setTimeout(() => cb(...args), ms);
  }, [cb, ms]);
};

export const FALLBACK_REPLY = "Got it. I'll keep this with the rest of today.";

/* Rotating "thinking" phrases shown while the AI is composing a reply.
   Twelve in total — cycled at 800ms intervals for a quiet, almost meditative
   feel rather than a spinner. */
export const THINKING_PHRASES = [
  'Reading between the lines…',
  'Turning this into your day log…',
  'Writing your reflection…',
  'Thinking about your flow…',
  'Capturing the essence…',
  'Weaving your day together…',
  'Listening to your words…',
  'Forming the picture…',
  'Recognizing the patterns…',
  'Breathing it in…',
  'Making sense of today…',
  'Writing what matters…',
];
